import * as THREE from "three";
import { NIGHT } from "@dyson/shared";

/** The wrong colour: cold teal, bright enough to bloom (see the palette rule for things from somewhere else). */
const SEAM = new THREE.Color(0.6, 2.4, 3.2);

let haloTexture: THREE.CanvasTexture | undefined;
function halo(): THREE.CanvasTexture {
  if (haloTexture) return haloTexture;
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,0.9)");
  g.addColorStop(0.35, "rgba(255,255,255,0.25)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  haloTexture = new THREE.CanvasTexture(canvas);
  return haloTexture;
}

const seamGeometry = new THREE.PlaneGeometry(0.22, 1);
const haloGeometry = new THREE.PlaneGeometry(1, 1);

/**
 * Rifts opening (see NIGHT.rift): a seam of light the wrong colour, a block
 * or two long, growing and flickering over the seconds it takes to open, with
 * a cold glow round it. Each is a seam and a glow; kept in a pool.
 */
export class Rifts {
  readonly group = new THREE.Group();
  private readonly pool: { seam: THREE.Mesh; glow: THREE.Mesh }[] = [];

  update(rifts: readonly [number, number, number][], time: number): void {
    while (this.pool.length < rifts.length) {
      const seam = new THREE.Mesh(seamGeometry, new THREE.MeshBasicMaterial({ color: SEAM, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
      const glow = new THREE.Mesh(haloGeometry, new THREE.MeshBasicMaterial({ map: halo(), color: new THREE.Color(0.3, 1.1, 1.5), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
      seam.position.z = glow.position.z = 0.9;
      this.group.add(glow, seam);
      this.pool.push({ seam, glow });
    }
    this.pool.forEach(({ seam, glow }, i) => {
      const r = rifts[i];
      seam.visible = glow.visible = !!r;
      if (!r) return;
      const [x, y, t] = r;
      const u = Math.min(1, t / NIGHT.rift.open);
      const flick = 0.75 + 0.25 * Math.sin(time * 31 + x) * Math.sin(time * 17 + y);
      seam.position.set(x, y, 0.9);
      glow.position.set(x, y, 0.85);
      seam.scale.set(0.6 + flick * 0.6, 0.3 + 2.2 * u * u, 1);
      seam.rotation.z = Math.sin(x * 12.9898 + y * 78.233) * 0.4;
      const s = 1.5 + 3.5 * u;
      glow.scale.set(s * 0.7, s, 1);
      (seam.material as THREE.MeshBasicMaterial).opacity = 0.5 + 0.5 * flick;
      (glow.material as THREE.MeshBasicMaterial).opacity = (0.35 + 0.4 * u) * flick;
    });
  }
}
